import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  Backdrop,
  LinearProgress,
  Paper,
  Card,
  Fade,
  CircularProgress,
  Portal,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import DownloadingIcon from "@mui/icons-material/Downloading";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import * as XLSX from "xlsx";
import axios from "axios";
import Swal from "sweetalert2";
import Apiurl from "../../Helpers/apiUrl";

const ExtractAllButton = ({ filters, totalCount, disabled }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedCount, setExtractedCount] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState("Calculating...");
  const [startTime, setStartTime] = useState(null);
  const abortControllerRef = useRef(null);

  // Calculate estimated time
  useEffect(() => {
    if (isDownloading && startTime && extractedCount > 0) {
      const elapsedSeconds = (Date.now() - startTime) / 1000;
      const speed = extractedCount / elapsedSeconds;

      if (speed > 0) {
        const remaining = totalCount - extractedCount;
        const secondsRemaining = Math.ceil(remaining / speed);

        if (secondsRemaining < 60) {
          setEstimatedTime(`${secondsRemaining} seconds`);
        } else if (secondsRemaining < 3600) {
          setEstimatedTime(`${Math.ceil(secondsRemaining / 60)} minutes`);
        } else {
          setEstimatedTime(`${Math.ceil(secondsRemaining / 3600)} hours`);
        }
      }
    }
  }, [isDownloading, startTime, extractedCount, totalCount]);

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsDownloading(false);
      setProgress(0);
      setExtractedCount(0);
      setEstimatedTime("Calculating...");
      setStartTime(null);

      Swal.fire({
        title: "Export Cancelled",
        text: "The export process has been stopped.",
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const handleExtract = async () => {
    if (totalCount === 0) {
      Swal.fire({
        title: "No Data Available",
        text: "There is no data to export based on current filters.",
        icon: "info",
        confirmButtonColor: "#1565c0",
      });
      return;
    }

    setIsDownloading(true);
    setProgress(0);
    setExtractedCount(0);
    setEstimatedTime("Calculating...");
    setStartTime(Date.now());
    abortControllerRef.current = new AbortController();

    const LIMIT = 5000;
    const totalPages = Math.ceil(totalCount / LIMIT);
    let allDownloadedData = [];
    const token = localStorage.getItem("u_token");

    try {
      for (let i = 0; i < totalPages; i++) {
        const response = await axios.get(
          `${Apiurl}admin/product/collection-quotation`,
          {
            params:{
              search: filters.search,
              page: i + 1,
              limit: LIMIT,
              fromDate: filters.from,
              toDate: filters.to,
              user: Array.isArray(filters.user) ? filters.user.join(",") : filters.user,
              client: Array.isArray(filters.client) ? filters.client.join(",") : filters.client,
              collection: Array.isArray(filters.collection) ? filters.collection.join(",") : filters.collection,
              sort_by: filters.sort_by,
              sort_order: filters.sort_order,
              role: Array.isArray(filters.role) ? filters.role.join(",") : filters.role,
              channel: Array.isArray(filters.channel) ? filters.channel.join(",") : filters.channel,
              month: Array.isArray(filters.month) ? filters.month.join(",") : filters.month,
              year: Array.isArray(filters.year) ? filters.year.join(",") : filters.year,
            },
            signal: abortControllerRef.current.signal,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const pageData = response.data?.data || [];
        allDownloadedData = [...allDownloadedData, ...pageData];

        setExtractedCount(allDownloadedData.length);
        setProgress(Math.round(((i + 1) / totalPages) * 100));
      }

      const sheetData = [
        [
          "SL No.",
          "Collection",
          "SKU Code",
          "Brand",
          "Description",
          "Finish",
          "Category",
          "Quantity",
          "MRP",
          "SKU Total",
          "Quotation ID",
          "Client",
          "Client's Region",
          "Client's State",
          "Channel",
          "Username",
          "User's Region",
          "User's State",
          "Role",
          "Valid Upto",
          "Stage",
          "Created At",
          "Month",
          "Year",
        ],
        ...allDownloadedData.map((item, index) => [
          index + 1,
          item.collection || "N/A",
          item.sku_code || "N/A",
          item.brand || "N/A",
          item.description || "N/A",
          item.finish || "N/A",
          item.category || "N/A",
          item.usages || 0,
          item.mrp !== undefined && item.mrp !== null ? Number(item.mrp) : 0,
          item.total !== undefined && item.total !== null ? Number(item.total) : 0,
          item.QuotationId || "N/A",
          item.client_name || "N/A",
          item.client_region || "N/A",
          item.client_state || "N/A",
          item.user_channel || "N/A",
          item.username || "N/A",
          item.user_region || item.user_resion || "N/A",
          item.user_state || item.state || "N/A",
          item.rolename || "N/A",
          item.valid_date ? new Date(item.valid_date).toLocaleDateString("en-GB") : "N/A",
          item.status || "N/A",
          item.created_at ? new Date(item.created_at).toLocaleString("en-GB") : "N/A",
          item.created_at ? new Date(item.created_at).toLocaleString("default", { month: "short" }) : "N/A",
          item.created_at ? new Date(item.created_at).getFullYear() : "N/A",
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      const wscols = [
        { wch: 8 },  // SL No.
        { wch: 20 }, // Collection
        { wch: 15 }, // SKU Code
        { wch: 15 }, // Brand
        { wch: 40 }, // Description
        { wch: 15 }, // Finish
        { wch: 15 }, // Category
        { wch: 12 }, // Quantity
        { wch: 15 }, // MRP
        { wch: 15 }, // SKU Total
        { wch: 20 }, // Quotation ID
        { wch: 25 }, // Client
        { wch: 18 }, // Client's Region
        { wch: 18 }, // Client's State
        { wch: 15 }, // Channel
        { wch: 20 }, // Username
        { wch: 18 }, // User's Region
        { wch: 18 }, // User's State
        { wch: 15 }, // Role
        { wch: 15 }, // Valid Upto
        { wch: 15 }, // Stage
        { wch: 20 }, // Created At
        { wch: 12 }, // Month
        { wch: 10 }, // Year
      ];
      ws["!cols"] = wscols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Quotation Report");

      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `Quotation_Report_${timestamp}.xlsx`;
      XLSX.writeFile(wb, fileName);

      Swal.fire({
        title: "Export Successful!",
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Records exported:</strong> ${allDownloadedData.length.toLocaleString()}</p>
            <p><strong>File name:</strong> ${fileName}</p>
          </div>
        `,
        icon: "success",
        confirmButtonColor: "#2e7d32",
        confirmButtonText: "OK",
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        Swal.fire({
          title: "Export Failed",
          text:
            error.message ||
            "Failed to fetch data for export. Please try again.",
          icon: "error",
          confirmButtonColor: "#d32f2f",
        });
      }
    } finally {
      setIsDownloading(false);
      setProgress(0);
      setEstimatedTime("Calculating...");
      setStartTime(null);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={isDownloading ? <DownloadingIcon /> : <TableChartIcon />}
        onClick={handleExtract}
        disabled={disabled || isDownloading || totalCount === 0}
        sx={{
          textTransform: "none",
          bgcolor: isDownloading ? "grey.600" : "#1565c0",
          "&:hover": {
            bgcolor: isDownloading ? "grey.600" : "#0d47a1",
            boxShadow: "0 4px 12px rgba(21, 101, 192, 0.3)",
          },
          py: '2px',
          px: '14px',
          borderRadius: 12,
          fontSize: "12px",
          transition: "all 0.2s ease-in-out",
          boxShadow: "0 2px 8px rgba(21, 101, 192, 0.2)",
          position: "relative",
          overflow: "hidden",
          "&::before": isDownloading
            ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
              animation: "shimmer 2s infinite",
            }
            : {},
        }}
      >
        {isDownloading ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
            Exporting ({progress}%)
          </>
        ) : (
          `Dump`
        )}
      </Button>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>

      <Portal>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          open={isDownloading}
        >
          <Fade in={isDownloading}>
            <Card
              sx={{
                width: 420,
                p: 0,
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  bgcolor: "#04121f",
                  p: 3,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <DownloadingIcon sx={{ fontSize: 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Exporting Report
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Please wait while we prepare your file
                  </Typography>
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3, bgcolor: "white" }}>
                {/* Progress Bar */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Export Progress
                    </Typography>
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ fontWeight: 700 }}
                    >
                      {progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: "grey.100",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        background: "linear-gradient(90deg, #1565c0, #2196f3)",
                      },
                    }}
                  />
                </Box>

                {/* Stats */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Records Processed
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FileDownloadDoneIcon
                        color="success"
                        sx={{ fontSize: 20 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {extractedCount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      of {totalCount.toLocaleString()} total
                    </Typography>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Time Remaining
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#ed6c02" }}
                    >
                      {estimatedTime}
                    </Typography>
                  </Paper>
                </Box>

                {/* Cancel Button */}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={handleCancel}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                      bgcolor: "error.light",
                      color: "error.dark",
                    },
                  }}
                >
                  Cancel Export
                </Button>

                {/* Important Note */}
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 2,
                    fontWeight: 500,
                    bgcolor: "error.50",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  ⚠️ Do not close this window while export is in progress
                </Typography>
              </Box>
            </Card>
          </Fade>
        </Backdrop>
      </Portal>
    </>
  );
};

export default ExtractAllButton;
